import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface TooltipState {
  /**
   * Persisted user preference. When true, the descriptive sidebar nav hover
   * tooltips (the on-hover feature copy) are shown; when false they stay hidden.
   *
   * Defaults to false: hover tips are an opt-in affordance the user enables via
   * the "Show tips" pill in the TopBar, rather than firing on every hover
   * (LIL-377). This supersedes the original first-visit-only behaviour (LIL-355).
   * This is the on-hover text — distinct from the coachmark tour (`useTourStore`).
   */
  tipsEnabled: boolean;
}

interface TooltipActions {
  /** Toggle the descriptive hover tooltips on or off. */
  toggleTips: () => void;
  /** Explicitly set whether the descriptive hover tooltips are shown. */
  setTipsEnabled: (enabled: boolean) => void;
}

export const useTooltipStore = create<TooltipState & TooltipActions>()(
  devtools(
    persist(
      set => ({
        tipsEnabled: false,
        toggleTips: () =>
          set(state => ({ tipsEnabled: !state.tipsEnabled }), false, 'tooltip/toggleTips'),
        setTipsEnabled: enabled => set({ tipsEnabled: enabled }, false, 'tooltip/setTipsEnabled'),
      }),
      {
        name: 'lili-tooltip-storage',
        version: 1,
        // v0 stored `hasVisited` (LIL-355). Existing users have already seen the
        // app, so default them to tips off; the TopBar pill lets them turn the
        // hover tips back on whenever they want.
        migrate: () => ({ tipsEnabled: false }),
        partialize: state => ({ tipsEnabled: state.tipsEnabled }),
      }
    ),
    {
      name: 'TooltipStore',
      enabled: import.meta.env.DEV,
    }
  )
);
